import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext.js'
import './LprEvents.css'

const today = () => new Date().toISOString().split('T')[0]

export default function LprEvents() {
  const { apiFetch, isOwner, isUser, admin } = useAuth()

  const [tenants,       setTenants]       = useState([])
  const [tenantId,      setTenantId]      = useState(isOwner ? null : admin?.tenant_id)
  const [events,        setEvents]        = useState([])
  const [loading,       setLoading]       = useState(false)
  const [dateFrom,      setDateFrom]      = useState(today)
  const [dateTo,        setDateTo]        = useState(today)
  const [webhookConfig, setWebhookConfig] = useState(null)
  const [copied,        setCopied]        = useState(false)

  useEffect(() => {
    if (!isOwner) return
    apiFetch('/api/admin/tenants').then(r => r.json()).then(data => {
      const list = Array.isArray(data) ? data : []
      setTenants(list)
      if (list.length > 0) setTenantId(list[0].id)
    }).catch(() => {})
  }, [isOwner])

  useEffect(() => {
    if (!isOwner && admin?.tenant_id) setTenantId(admin.tenant_id)
  }, [isOwner, admin?.tenant_id])

  const loadEvents = useCallback(() => {
    if (!tenantId) return
    setLoading(true)
    const qs = new URLSearchParams()
    if (dateFrom) qs.set('from', dateFrom)
    if (dateTo)   qs.set('to',   dateTo)
    apiFetch(`/api/admin/lpr-events/${tenantId}?${qs}`)
      .then(r => r.json())
      .then(data => setEvents(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [tenantId, dateFrom, dateTo])

  useEffect(() => { loadEvents() }, [loadEvents])

  // Not for the user role: the webhook key opens barriers, and the server refuses it.
  const loadWebhookConfig = useCallback(() => {
    if (!tenantId || isUser) return
    apiFetch(`/api/admin/lpr-webhook-config/${tenantId}`)
      .then(r => r.json())
      .then(data => setWebhookConfig(data))
      .catch(() => {})
  }, [tenantId, isUser])

  useEffect(() => { loadWebhookConfig() }, [loadWebhookConfig])

  const copyUrl = () => {
    if (!webhookConfig?.webhook_url) return
    navigator.clipboard.writeText(webhookConfig.webhook_url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const formatTime = (iso) =>
    iso ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'medium' }) : '—'

  const directionLabel = (direction) => {
    if (direction === 'in')  return <span title="Einfahrt">→ Ein</span>
    if (direction === 'out') return <span title="Ausfahrt">← Aus</span>
    return <span className="lpr-meta">—</span>
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Kennzeichen-Scans</h1>
          <p>Protokoll aller von der AXIS-Kamera erkannten Kennzeichen</p>
        </div>
      </div>

      {/* Tenant selector */}
      {isOwner && tenants.length > 0 && (
        <div className="card lpr-tenant-card">
          <div className="form-group">
            <label>Mandant</label>
            <select className="form-control" value={tenantId || ''}
              onChange={e => setTenantId(e.target.value)}>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Webhook config */}
      {webhookConfig && !isUser && (
        <div className="card lpr-webhook-card">
          <div className="section-title">Kamera-Webhook</div>
          <p className="sm-hint">
            Diese URL in der AXIS-Kamera unter <strong>Apps → License Plate Verifier → Push-Einstellungen</strong> eintragen.
          </p>
          <div className="lpr-webhook-row">
            <input
              className="form-control lpr-webhook-input"
              value={webhookConfig.webhook_url}
              readOnly
            />
            <button className="btn btn-ghost" onClick={copyUrl}>
              {copied ? '✓ Kopiert' : 'Kopieren'}
            </button>
          </div>
        </div>
      )}

      {/* Events table */}
      <div className="card">
        <div className="lpr-controls">
          <div className="lpr-date-row">
            <input type="date" className="form-control lpr-date"
              value={dateFrom} max={dateTo}
              onChange={e => setDateFrom(e.target.value)} />
            <span className="lpr-sep">–</span>
            <input type="date" className="form-control lpr-date"
              value={dateTo} min={dateFrom}
              onChange={e => setDateTo(e.target.value)} />
          </div>
          <button className="btn btn-ghost" onClick={loadEvents} disabled={loading}>
            {loading ? <><span className="spinner spinner-sm" /> Laden…</> : 'Aktualisieren'}
          </button>
        </div>

        {loading ? (
          <div className="loading-state"><span className="spinner" /></div>
        ) : events.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📷</div>
            <p>Keine Erkennungen im gewählten Zeitraum.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="lpr-table">
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Kennzeichen</th>
                  <th>Land</th>
                  <th>Richtung</th>
                  <th>Ergebnis</th>
                </tr>
              </thead>
              <tbody>
                {events.map(e => (
                  <tr key={e.id}>
                    <td className="lpr-time">{formatTime(e.scanned_at)}</td>
                    <td className="lpr-plate">{e.plate}</td>
                    <td className="lpr-meta">{e.country || '—'}</td>
                    <td className="lpr-meta">{directionLabel(e.direction)}</td>
                    <td>
                      {e.matched === true  && <span className="lpr-badge lpr-badge--allow">Erlaubt</span>}
                      {e.matched === false && <span className="lpr-badge lpr-badge--deny">Nicht erlaubt</span>}
                      {e.matched === null  && <span className="lpr-meta">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {events.length > 0 && (
          <p className="lpr-count">{events.length} Einträge</p>
        )}
      </div>
    </div>
  )
}
